import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  class: "flex flex-col w-full h-full min-h-[420px] rounded-lg border border-[#d8d8e2] bg-[#f0f0f4] overflow-hidden",
  "data-testid": "document-viewer"
}
const _hoisted_2 = { class: "flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-[#e8e8ed] text-[12px] text-[#52526f]" }
const _hoisted_3 = { class: "truncate font-semibold text-textPrimary" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center gap-1 shrink-0"
}
const _hoisted_5 = { class: "mr-2" }
const _hoisted_6 = ["aria-label", "data-testid", "onClick"]
const _hoisted_7 = {
  key: 0,
  class: "flex-1 flex flex-col items-center justify-center gap-2 p-6 text-center",
  "data-testid": "document-viewer-empty"
}
const _hoisted_8 = { class: "text-sm font-semibold text-textPrimary" }
const _hoisted_9 = { class: "text-xs text-[#7f7f8e] max-w-xs" }
const _hoisted_10 = {
  key: 1,
  class: "flex-1 flex items-center justify-center",
  "data-testid": "document-viewer-loading"
}
const _hoisted_11 = {
  key: 2,
  class: "flex-1 flex items-center justify-center p-6 text-sm text-negative",
  role: "alert",
  "data-testid": "document-viewer-error"
}
const _hoisted_12 = {
  key: 3,
  class: "relative flex-1 min-h-[360px]"
}
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", { class: "px-3 py-1.5 text-[11px] text-[#7f7f8e] bg-white border-t border-[#e8e8ed] text-left" }, " Visualização com marca d'água de quem está vendo. Download desabilitado. ", -1)

import { computed, defineAsyncComponent, ref, toRef, watch } from 'vue'
import {
  mdiFileHidden,
  mdiMagnifyMinusOutline,
  mdiMagnifyPlusOutline,
  mdiRotateRight
} from '@mdi/js'

import { useDocumentFileUrl } from '@/composables/useDocumentFileUrl'

// Carga sob demanda: o `PdfRenderer` arrasta o `pdfjs-dist` (mesmo padrão do `StepPreview`).

export default /*#__PURE__*/_defineComponent({
  __name: 'DocumentViewer',
  props: {
    versionId: {},
    isLegacy: { type: Boolean, default: false },
    isRestricted: { type: Boolean, default: false },
    pageCount: { default: null },
    sizeBytes: { default: null }
  },
  setup(__props: any) {

const props = __props;

const PdfRenderer = defineAsyncComponent(
  () => import('@/components/designSystem/modals/AttendanceUploadModal/PdfRenderer.vue')
)



const { url, isLoading, error } = useDocumentFileUrl(toRef(props, 'versionId'))

const zoom = ref(1)
const rotation = ref(0)
const page = ref(1)

watch(
  () => props.versionId,
  () => {
    zoom.value = 1
    rotation.value = 0
    page.value = 1
  }
)

const CONTROLS = [
  {
    testId: 'document-viewer-zoom-out',
    label: 'Diminuir zoom',
    icon: mdiMagnifyMinusOutline,
    apply: () => (zoom.value = Math.max(0.6, +(zoom.value - 0.2).toFixed(1)))
  },
  {
    testId: 'document-viewer-zoom-in',
    label: 'Aumentar zoom',
    icon: mdiMagnifyPlusOutline,
    apply: () => (zoom.value = Math.min(2, +(zoom.value + 0.2).toFixed(1)))
  },
  {
    testId: 'document-viewer-rotate',
    label: 'Girar',
    icon: mdiRotateRight,
    apply: () => (rotation.value = (rotation.value + 90) % 360)
  }
]

const emptyState = computed(() => {
  if (props.isRestricted) {
    return {
      title: 'Arquivo não compartilhado com a sua empresa',
      text: 'O status do documento continua visível, mas o arquivo só aparece para quem o médico autorizou.'
    }
  }
  if (props.isLegacy) {
    return {
      title: 'Concluído (legado) — sem arquivo',
      text: 'Médico com vínculo anterior à data de corte: nunca bloqueia. O Drive convida a enviar.'
    }
  }
  return {
    title: 'O médico ainda não enviou este documento',
    text: 'Quando ele anexar pelo app, o documento entra direto em Em Análise.'
  }
})

const metaText = computed(() => {
  const parts = ['PDF']
  if (props.pageCount) {
    parts.push(`${props.pageCount} ${props.pageCount === 1 ? 'página' : 'páginas'}`)
  }
  if (props.sizeBytes) {
    parts.push(`${(props.sizeBytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`)
  }
  return parts.join(' · ')
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.versionId ? 'Arquivo enviado' : 'Sem arquivo'), 1),
      (_unref(url))
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(metaText.value), 1),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(CONTROLS, (control) => {
              return _createElementVNode("button", {
                key: control.testId,
                type: "button",
                class: "p-1 rounded hover:bg-gray-100",
                "aria-label": control.label,
                "data-testid": control.testId,
                onClick: control.apply
              }, [
                _createVNode(_component_v_icon, {
                  icon: control.icon,
                  size: "16"
                }, null, 8, ["icon"])
              ], 8, _hoisted_6)
            }), 64))
          ]))
        : _createCommentVNode("", true)
    ]),
    (!_ctx.versionId)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createVNode(_component_v_icon, {
            icon: _unref(mdiFileHidden),
            size: "40",
            class: "text-[#bcbccd]"
          }, null, 8, ["icon"]),
          _createElementVNode("p", _hoisted_8, _toDisplayString(emptyState.value.title), 1),
          _createElementVNode("p", _hoisted_9, _toDisplayString(emptyState.value.text), 1)
        ]))
      : (_unref(isLoading))
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createVNode(_component_v_progress_circular, {
              indeterminate: "",
              color: "primary"
            })
          ]))
        : (_unref(error))
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_unref(error)), 1))
          : (_unref(url))
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createVNode(_unref(PdfRenderer), {
                  page: page.value,
                  "onUpdate:page": _cache[0] || (_cache[0] = ($event: any) => ((page).value = $event)),
                  src: _unref(url),
                  scale: zoom.value,
                  rotation: rotation.value,
                  "allow-open-in-new-tab": false
                }, null, 8, ["page", "src", "scale", "rotation"])
              ]))
            : _createCommentVNode("", true),
    _hoisted_13
  ]))
}
}

})